import { Dialog, DialogTitle, DialogContent, Box, CircularProgress } from '@mui/material'
import { useContext, useEffect, useState } from 'react'

import Text from '../../../assets/components/Text'
import BasicButton from '../../../assets/components/BasicButton'
import { usePostSendOTARebootRequest } from '../../../mqtt/mqttHandlerContext'

import { OTAUpdatePayload, OTA_STATUS } from '../../../utils/otaTypes'
import { mapOTADataToViewState, OTAViewState } from './otaHelpers'
import { PolyglotContext } from '../../../polyglot/polyGlotContext'

interface OTADialogProps {
    openOTADialog: boolean
    otaUpdateData: OTAUpdatePayload
    onClose: () => void
    deviceId: string
}

const OTADialog = ({ openOTADialog, otaUpdateData, onClose, deviceId }: OTADialogProps) => {
    const { polyglot } = useContext(PolyglotContext)
    const rebootInstrument = usePostSendOTARebootRequest()
    const [isRebooting, setIsRebooting] = useState(false)

    const [lastUpdateTime, setLastUpdateTime] = useState(Date.now())
    const [hasTimedOut, setHasTimedOut] = useState(false)

    const baseViewState = mapOTADataToViewState(otaUpdateData, polyglot)

    // -------------------------
    // Update timestamp on every message
    // -------------------------
    useEffect(() => {
        if (otaUpdateData) {
            setLastUpdateTime(Date.now())
            setHasTimedOut(false)
        }
    }, [otaUpdateData])

    // -------------------------
    // ⏱Detect stalled OTA (no updates)
    // -------------------------
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now()

            const isStalled =
                now - lastUpdateTime > 10000 && // 10 seconds
                baseViewState.type === 'progress' &&
                !isRebooting

            if (isStalled) {
                setHasTimedOut(true)
            }
        }, 1000)

        return () => clearInterval(interval)
    }, [lastUpdateTime, baseViewState.type, isRebooting])

    // -------------------------
    // Handle reboot
    // -------------------------
    const handleReboot = () => {
        try {
            setIsRebooting(true)
            rebootInstrument(deviceId)
        } catch (error) {
            console.error('Reboot failed:', error)
            setIsRebooting(false)
        }
    }

    // -------------------------
    // Exit rebooting when UPDATED arrives
    // -------------------------
    useEffect(() => {
        if (otaUpdateData.type === 'progress' && otaUpdateData.data.state === OTA_STATUS.UPDATED) {
            setIsRebooting(false)
        }
    }, [otaUpdateData])

    // -------------------------
    // ⏱️ Safety timeout for reboot
    // -------------------------
    useEffect(() => {
        if (isRebooting) {
            const timeout = setTimeout(() => {
                setIsRebooting(false)
            }, 15000)

            return () => clearTimeout(timeout)
        }
    }, [isRebooting])

    // -------------------------
    // 🔥 Final viewState override logic
    // -------------------------
    let viewState: OTAViewState = baseViewState

    if (isRebooting) {
        viewState = {
            type: 'rebooting',
            label: polyglot.t('ota.rebooting')
        }
    } else if (hasTimedOut) {
        const wasDownloading = baseViewState.type === 'progress' && baseViewState.progress !== undefined

        viewState = {
            type: 'error',
            label: wasDownloading ? polyglot.t('ota.rebootingFailed') : polyglot.t('ota.noResponse'),
            retryable: true
        }
    }

    // -------------------------
    // Render
    // -------------------------
    const renderContent = () => {
        switch (viewState.type) {
            case 'progress':
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" gap="1rem">
                        <Text>{viewState.label}</Text>

                        {viewState.progress !== undefined && <Text>{`${Math.round(viewState.progress * 100)} %`}</Text>}
                    </Box>
                )

            case 'rebooting':
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" gap="1rem">
                        <Text>{viewState.label}</Text>
                        <CircularProgress />
                        <Text>{polyglot.t('ota.rebootLoading')}</Text>
                    </Box>
                )

            case 'success':
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" gap="1rem">
                        <Text>{viewState.label}</Text>

                        {viewState.action === 'reboot' && <BasicButton title={polyglot.t('ota.rebootBtnTitle')} variant="contained" onClick={handleReboot} />}

                        {viewState.action === 'close' && <BasicButton title={polyglot.t('ota.btnCloseTitle')} variant="contained" onClick={onClose} />}
                    </Box>
                )

            case 'error':
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" gap="1rem" textAlign="center">
                        <Text>{viewState.label}</Text>

                        <BasicButton title={polyglot.t('ota.btnCloseTitle')} variant="outlined" onClick={onClose} />
                    </Box>
                )

            default:
                return null
        }
    }

    return (
        <Dialog
            open={openOTADialog}
            onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return
                }
                onClose
            }}
            fullWidth
        >
            <DialogTitle sx={{ textAlign: 'center' }}>{polyglot.t('ota.dialogTitle')}</DialogTitle>

            <DialogContent
                sx={{
                    margin: '1rem',
                    display: 'flex',
                    justifyContent: 'center'
                }}
            >
                {renderContent()}
            </DialogContent>
        </Dialog>
    )
}

export default OTADialog
